import React, { useEffect, useMemo, useState } from 'react'
import SlideBase from './SlideBase'
import WrappedVideoBackground from '../WrappedVideoBackground'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'

// Get video config for this slide (static, doesn't depend on runtime data)
const SLIDE_VIDEO = WRAPPED_SLIDES.find(s => s.id === 'offer-views')?.video
const SLIDE_ID = 'offer-views' as const

function OfferViewsSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)
  const isTotalsOnlyMode = useWrappedStorySelector(state => state.isTotalsOnlyMode)
  const isVideoReady = useWrappedStorySelector(state => state.isVideoReady)
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)
  const [animate, setAnimate] = useState(!SLIDE_VIDEO) // If no video, animate immediately

  // Start animation when video is ready (only when this is the current slide)
  useEffect(() => {
    if (SLIDE_VIDEO && isVideoReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isVideoReady, isCurrentSlide])

  // Memoize background to prevent recreation - must be before conditional return for hook rules
  const videoBackground = useMemo(() => {
    if (!SLIDE_VIDEO) return undefined
    return <WrappedVideoBackground video={SLIDE_VIDEO} isActive={isCurrentSlide} />
  }, [isCurrentSlide])

  if (!data) return null

  const { stats } = data
  const totalOffersViewed = totalsData?.stats.offersViewed ?? 0
  const averageOffersViewed = totalsData?.stats.averageOffersViewed ?? stats.averageOffersViewed

  // Show totals version if in totals-only mode
  if (isTotalsOnlyMode) {
    return (
      <SlideBase background={videoBackground}>
        {animate && <FadeBackground>
          <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">Offers Viewed</Caption>
              <Heading variant="heading5">
                <b>
                  In 2025, Luxury Escapes travellers explored
                </b>
              </Heading>
            </Group>
            <Heading variant="heading1alt">
              <FormatDecimal value={totalOffersViewed} /> offers
            </Heading>
          </Group>
        </FadeBackground>}
      </SlideBase>
    )
  }

  return (
    <SlideBase background={videoBackground}>
      {animate && <FadeBackground>
        <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
          <Group direction="vertical" gap={4}>
            <Caption colour="highlight-primary" variant="small" format="uppercase">Offers Viewed</Caption>
            <Heading variant="heading5">
              <b>
                You found inspiration in
              </b>
            </Heading>
          </Group>
          <Group direction="vertical" gap={4}>
            <Heading variant="heading1alt">
              <FormatDecimal value={stats.offersViewed} /> offers
            </Heading>
            <BodyText variant="medium">
              On average, our travellers viewed <b><FormatDecimal value={averageOffersViewed} /> offers</b> last year.
            </BodyText>
          </Group>
        </Group>
      </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(OfferViewsSlide)
